import React, { useLayoutEffect, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import qs from 'query-string'
import cn from 'classnames'
import { mediaXsOnly, mediaMaxMd } from '@gymondo/frontend-core/style-vars'
import { useMediaQuery } from '@gymondo/frontend-core/hooks'
import { graphql } from 'gatsby'
import _ from 'lodash'
import { useIntl } from 'react-intl'
import { FluidWrapper, ScrollTopArrow } from '@gymondo/frontend-core/components'
import Typography from '@gymondo/frontend-core/typography'
import {
    setLoginModalChangePassword,
    openLoginModal,
} from '../../../service/page-header'
import { setConfig } from '../../../service/layout'
import { BackgroundImage, Cta } from '../../../component'
import ProgramGrid from './component/program-grid'
import { QUERY_PARAMS } from '../../../config/definitions'
import TrustPilotLink from '../../../component/trust-pilot-link/trust-pilot-link'
import headerTitleDesktopEn from './static/image/header_title_en.png'
import headerTitleDesktopDe from './static/image/header_title_de.png'
import headerTitleMobileEn from './static/image/header_title_mobile_en.png'
import headerTitleMobileDe from './static/image/header_title_mobile_de.png'

import style from './home.module.scss'

const renderHero = (img, titleImg, isXs, loaded, intl) => {
    return (
        <FluidWrapper
            wrapperClassName={style.heroContentWrapper}
            className={style.heroContent}
        >
            <BackgroundImage
                classParentName={style.heroContainer}
                childrenClassName={style.heroChildContainer}
                fluid={img}
                imgClassName={style.imgClassName}
                gradient
            >
                <div className={style.heroTextWrapper}>
                    {loaded && (
                        <>
                            <Typography
                                as="h2"
                                variant="h2"
                                color="black"
                                className={cn(style.heroText, style.heroTitle)}
                            >
                                {intl.formatMessage({ id: 'home.hero.title' })}
                            </Typography>
                            <Typography
                                as="h6"
                                variant="h6"
                                color="black"
                                className={cn(style.heroSubtitle)}
                                dangerouslySetInnerHTML={{
                                    __html: intl.formatMessage({
                                        id: 'home.hero.subtitle',
                                    }),
                                }}
                            />
                            <Cta displayXS fixedXS className={style.heroCta} />
                        </>
                    )}
                </div>
            </BackgroundImage>
        </FluidWrapper>
    )
}

const renderFooter = (img, intl) => {
    return (
        <FluidWrapper className={cn(style.heroContent, style.footerContent)}>
            <BackgroundImage
                fluid={img}
                imgClassName={style.footerImgClassName}
                classParentName={style.footerBannerContainer}
            >
                <Typography
                    as="h2"
                    variant="h2"
                    className={cn(style.heroText, style.heroTitle)}
                >
                    {intl.formatMessage({ id: 'home.footer.title' })}
                </Typography>
                <Typography
                    as="h6"
                    variant="h6"
                    className={cn(style.heroSubtitle)}
                    dangerouslySetInnerHTML={{
                        __html: intl.formatMessage({
                            id: 'home.footer.subtitle',
                        }),
                    }}
                />
            </BackgroundImage>
        </FluidWrapper>
    )
}

const renderProgramsGrid = (localePrograms) => (
    <div>
        <div id="programs" className="wrapper">
            <ProgramGrid preFetchedPrograms={localePrograms} />
        </div>
    </div>
)

const getNodeName = (node) => `${node}.edges.0.node.childImageSharp.fluid`

/**
 * Home page
 */
export const Home = ({
    data,
    pageContext,
    location,
    setLoginModalChangePasswordAction,
    openLoginModalAction,
    setLayoutConfig,
    user,
}) => {
    const intl = useIntl()
    const isMaxMd = useMediaQuery(mediaMaxMd)
    const isXs = useMediaQuery(mediaXsOnly)
    const { locale } = pageContext
    const isDe = locale === 'de'

    const programs = _.groupBy(_.get(data, 'allProgram.nodes'), 'locale')
    const localePrograms = programs[locale] || []

    const headerTitleEn = isXs ? headerTitleMobileEn : headerTitleDesktopEn
    const headerTitleDe = isXs ? headerTitleMobileDe : headerTitleDesktopDe

    const heroTitleImg = (
        <img
            src={isDe ? headerTitleDe : headerTitleEn}
            className={style.heroTitleImg}
            alt={intl.formatMessage({ id: 'home.hero.title' })}
        />
    )

    let heroImg

    let footerImg
    if (isXs) {
        heroImg = _.get(data, getNodeName('headerMobileDe'))
        footerImg = isDe
            ? _.get(data, getNodeName('footerMobileDe'))
            : _.get(data, getNodeName('footerMobile'))
    } else if (isMaxMd) {
        heroImg = _.get(data, getNodeName('headerMobileDe'))
        footerImg = isDe
            ? _.get(data, getNodeName('footerTabletDe'))
            : _.get(data, getNodeName('footerTablet'))
    } else {
        heroImg = _.get(data, getNodeName('headerDe'))
        footerImg = isDe
            ? _.get(data, getNodeName('footerDe'))
            : _.get(data, getNodeName('footerEn'))
    }

    useLayoutEffect(() => {
        setLayoutConfig({
            className: style.layout,
            textColorVariant: 'white',
        })
    }, [setLayoutConfig])

    useLayoutEffect(() => {
        const queryParams = qs.parse(location.search)
        if (user.isLoggedIn && queryParams[QUERY_PARAMS.FORWARD_URL]) {
            window.location.assign(queryParams[QUERY_PARAMS.FORWARD_URL])
        } else if (
            queryParams[QUERY_PARAMS.SHOW_LOGIN] === 'true' ||
            queryParams[QUERY_PARAMS.SHOW_LOGIN_LEGACY] === 'true'
        ) {
            openLoginModalAction(true)
        } else if (queryParams[QUERY_PARAMS.RESET_PASSWORD_CODE]) {
            setLoginModalChangePasswordAction()
            openLoginModalAction()
        }
    })

    const [loaded, setLoaded] = useState(false)
    useEffect(() => {
        setLoaded(true)
    }, [])

    return (
        <div>
            {renderHero(heroImg, heroTitleImg, isXs, loaded, intl)}

            <TrustPilotLink locale={locale} />

            {renderProgramsGrid(localePrograms)}
            {renderFooter(footerImg, intl)}
            <ScrollTopArrow />
        </div>
    )
}

Home.propTypes = {
    data: PropTypes.object.isRequired,
    pageContext: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    setLoginModalChangePasswordAction: PropTypes.func.isRequired,
    openLoginModalAction: PropTypes.func.isRequired,
    setLayoutConfig: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
}

export default connect(
    ({ user }) => ({
        user,
    }),
    {
        setLoginModalChangePasswordAction: setLoginModalChangePassword,
        openLoginModalAction: openLoginModal,
        setLayoutConfig: setConfig,
    }
)(Home)

export const query = graphql`
    {
        headerDe: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/header_de.jpeg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 3840
                            srcSetBreakpoints: [1900, 2200, 3840]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        headerMobileDe: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/header_mobile_de.jpeg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1400
                            srcSetBreakpoints: [1400, 1000]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerDe: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_de.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 3840
                            srcSetBreakpoints: [1400, 1900, 2200, 3840]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerEn: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_en.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 3840
                            srcSetBreakpoints: [1400, 1900, 2200, 3840]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerMobile: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_mobile.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1400
                            srcSetBreakpoints: [1400, 1000]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerMobileDe: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_mobile_de.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1400
                            srcSetBreakpoints: [1400, 1000]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerTablet: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_tablet.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1400
                            srcSetBreakpoints: [1400, 1000]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        footerTabletDe: allFile(
            filter: {
                relativePath: {
                    eq: "page/generic/home/static/image/footer_tablet_de.jpg"
                }
            }
        ) {
            edges {
                node {
                    childImageSharp {
                        fluid(
                            maxWidth: 1400
                            srcSetBreakpoints: [1400, 1000]
                            toFormat: NO_CHANGE
                            quality: 90
                        ) {
                            ...GatsbyImageSharpFluid_withWebp
                        }
                    }
                }
            }
        }
        allProgram(sort: { order: ASC, fields: [sortIndex] }) {
            nodes {
                id
                durationWeeks
                headline
                locale
                level
                imageUrl
                sortIndex
                slug
                goal {
                    name
                    color
                }
                new
                details {
                    imagesNode {
                        background {
                            childImageSharp {
                                fluid(
                                    maxWidth: 965
                                    srcSetBreakpoints: [476, 965]
                                    toFormat: NO_CHANGE
                                    quality: 90
                                ) {
                                    ...GatsbyImageSharpFluid_withWebp
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
